import { useMutation } from '@apollo/client';
import { DeleteCommentDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import type { DeleteCommentTRPCInput } from './useDeleteCommentTRPC';
import { useDeleteCommentTRPC } from './useDeleteCommentTRPC';

/**
 * Flag-gated wrapper (RSP-6496). Both paths return `{ Id }`.
 */
export const useDeleteComment = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [deleteCommentGraphQL, graphqlState] = useMutation(
    DeleteCommentDocument
  );

  const trpcMutation = useDeleteCommentTRPC();

  const deleteComment = async (
    input: DeleteCommentTRPCInput
  ): Promise<{ Id: string }> => {
    if (trpcEnabled) {
      return trpcMutation.deleteComment(input);
    }

    const result = await deleteCommentGraphQL({
      variables: { Id: input.Id },
    });
    if (!result.data?.delete_comment_by_pk) {
      throw new Error('Failed to delete comment');
    }

    return { Id: result.data.delete_comment_by_pk.Id };
  };

  if (trpcEnabled) {
    return {
      deleteComment,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    deleteComment,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
